import { Children, useCallback } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

// The light entry point: the package root would pull in the charts.
import { Pagination, needsPager, useClientPage } from "@strat/ui/pagination";

/**
 * Grid, list or details: one collection, three densities.
 *
 * - Grid and List show the same card; List lays it out as a full-width row,
 *   for scanning down a long collection.
 * - Details is a sortable table: every number in a column, for comparing.
 *
 * The choice is remembered per page in this browser (a convenience, so it may
 * be lost; the page still works) and mirrored in the address as `?view=…`, so a
 * link someone shares opens the view they were looking at.
 */
export type ViewMode = "grid" | "list" | "details";
const MODES: readonly ViewMode[] = ["grid", "list", "details"];

const storageKey = (page: string) => `strategyhive.view.${page}`;

function stored(page: string): ViewMode | null {
  try {
    const v = localStorage.getItem(storageKey(page));
    return MODES.includes(v as ViewMode) ? (v as ViewMode) : null;
  } catch {
    return null;
  }
}

export function useViewMode(page: string, fallback: ViewMode): [ViewMode, (mode: ViewMode) => void] {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("view");
  const mode = MODES.includes(fromUrl as ViewMode) ? (fromUrl as ViewMode) : (stored(page) ?? fallback);
  const set = useCallback(
    (next: ViewMode) => {
      try {
        localStorage.setItem(storageKey(page), next);
      } catch {
        // Private windows and blocked storage: the URL still carries it.
      }
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === fallback) p.delete("view");
          else p.set("view", next);
          return p;
        },
        { replace: true },
      );
    },
    [page, fallback, setParams],
  );
  return [mode, set];
}

const ICON: Record<ViewMode, ReactNode> = {
  grid: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" fill="currentColor" />
      <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" fill="currentColor" />
      <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" fill="currentColor" />
      <rect x="9" y="9" width="5.5" height="5.5" rx="1" fill="currentColor" />
    </svg>
  ),
  list: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <rect x="1.5" y="2" width="13" height="3.5" rx="1" fill="currentColor" />
      <rect x="1.5" y="6.5" width="13" height="3.5" rx="1" fill="currentColor" />
      <rect x="1.5" y="11" width="13" height="3.5" rx="1" fill="currentColor" />
    </svg>
  ),
  details: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <rect x="1.5" y="2" width="13" height="2" rx="0.6" fill="currentColor" />
      <rect x="1.5" y="5.5" width="13" height="1.4" rx="0.5" fill="currentColor" />
      <rect x="1.5" y="8.5" width="13" height="1.4" rx="0.5" fill="currentColor" />
      <rect x="1.5" y="11.5" width="13" height="1.4" rx="0.5" fill="currentColor" />
    </svg>
  ),
};
const LABEL: Record<ViewMode, string> = { grid: "Grid", list: "List", details: "Details" };

export function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (mode: ViewMode) => void }) {
  return (
    <div className="segmented view-toggle" role="group" aria-label="View">
      {MODES.map((m) => (
        <button
          key={m}
          type="button"
          className={value === m ? "is-active" : undefined}
          aria-pressed={value === m}
          title={`${LABEL[m]} view`}
          onClick={() => onChange(m)}
        >
          {ICON[m]}
          <span className="view-toggle__label">{LABEL[m]}</span>
        </button>
      ))}
    </div>
  );
}

/** The wrapper for cards in grid or list view. */
export function CardCollection({
  mode,
  children,
  busy,
}: {
  mode: "grid" | "list";
  children: ReactNode;
  busy?: boolean;
}) {
  // Twenty cards a page, like the Details table of the same collection.
  const cards = Children.toArray(children);
  const page = useClientPage(cards);
  return (
    <>
      <div className={`card-collection card-collection--${mode}${busy ? " is-refreshing" : ""}`}>
        {needsPager(cards.length) ? page.pageItems : cards}
      </div>
      {needsPager(cards.length) && <Pagination {...page.pager} />}
    </>
  );
}
